<template>
  <div :class="cardClass">
    <div class="smart-card__imageContainer">
      <c-image
        v-if="type !== 'tertiary'"
        class="smart-card__image"
        :style="{ justifyContent: imageAlign }"
        :image-mobile="imageMobile"
        :image-tablet="imageTablet"
        :image-desktop="imageDesktop"
        :alt="imageAlt"
      />
    </div>
    <div
      v-if="showVideo"
      class="smart-card__videoContainer"
    >
      <div
        class="smart-card__video"
      >
        <slot name="video" />
      </div>
    </div>
    <c-text
      class="smart-card__title"
      :color="titleColor"
      :align="titleAlign"
    >
      <c-icon
        v-if="iconName"
        :size="iconSize"
        :color="iconColor"
        :name="iconName"
      />
      <slot name="title" />
    </c-text>
    <div class="smart-card__text">
      <c-text
        color="#43515A"
        size="xs"
        weight="400"
        :align="textAlign"
      >
        <p>
          <slot name="text" />
        </p>
      </c-text>
    </div>
    <div
      v-if="type === 'primary' || 'tertiary'"
      class="smart-card__buttons"
    >
      <slot name="buttons" />
    </div>
  </div>
</template>

<script>
import CIcon from '@ions/icon/icon.vue'
import CImage from '@atoms/image/image.vue'
import CText from '@atoms/text/text.vue'

export default {
  name: 'smart-card',
  components: {
    CIcon,
    CImage,
    CText,
  },
  props: {
    imageMobile: {
      type: String,
    },
    imageTablet: {
      type: String,
    },
    imageDesktop: {
      type: String,
    },
    imageAlt: {
      type: String,
    },
    imageAlign: {
      type: String,
      default: 'left'
    },
    iconName: {
      type: String,
      default: '',
    },
    iconColor: {
      type: String,
      default: '#074E8E',
    },
    iconSize: {
      type: String,
      default: 'lg',
    },
    titleColor: {
      type: String,
      default: '#000000'
    },
    titleAlign: {
      type: String,
      default: 'left'
    },
    textAlign: {
      type: String,
      default: 'left'
    },
    type: {
      type: String,
      default: 'primary',
      validator(value) {
        return ['primary', 'secondary', 'tertiary', 'quaternary'].includes(
          value
        )
      },
    },
    experience: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    cardClass() {
      return ['smart-card', `smart-card--${this.type}`, { 'smart-card--experience': this.experience }]
    },
    showVideo() {
      return (this.type === 'primary' || this.type === 'secondary') && !this.imageMobile && !this.imageTablet && !this.imageDesktop
    }
  },
}
</script>

<style lang="scss" scoped>
.smart-card {
  display: flex;
  flex-direction: column;
  width: 100%;

  &__imageContainer {
    display: flex;
    overflow: hidden;
  }

  &__image {
    display: flex;
    transition: all 0.8s;
    @include textAlign();

    &:hover {
      transform: scale(1.08);
    }
  }

  &__video, &__videoContainer {
    height: -webkit-fill-available;
    overflow: hidden;
  }

  &__video {
    display: flex;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    @include textAlign();

    @include font-telegraf(400);
    font-size: 1.5rem;
    margin-top: 1rem;
    margin-bottom: 0.5rem;

    @include screen-size('xl') {
      margin-bottom: 1rem;
    }
  }

  &__text {
    margin-bottom: 1rem;

    @include screen-size('xl') {
      margin-bottom: 1.313rem;
    }
  }

  &__buttons {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    @include screen-size('xs') {
      flex-direction: row;
    }
  }

  &--quaternary {
    .smart-card__image {
      &:hover {
        transform: none;
      }
    }
  }
  &--experience {
    .smart-card__image, .smart-card__video {
      &:hover {
        transform: none;
      }
    }
  }
}
</style>
